<script setup lang="ts">
import { LoaderCircle, ClipboardCheck } from "lucide-vue-next";
import { renderMarkdown } from "@/lib/markdown";
import { computed } from "vue";

import type { GradeResponse } from "@/api/types";
import RlCard from "@/components/ui/RlCard.vue";
import RlPill from "@/components/ui/RlPill.vue";
import RlSkeleton from "@/components/ui/RlSkeleton.vue";

/**
 * The marking half of the question surface (PE-8.3).
 *
 * Purely presentational: the grade arrives as a prop and this component never
 * calls the API. Three states — empty, marking, marked. The marking state is
 * one of only two real waits in the product (the other is question
 * generation), so it gets an explicit status line plus a shimmer skeleton
 * shaped like the result that is coming, rather than a bare spinner.
 *
 * Prior attempts stay visible above the current grade (PRD §6.7): a student
 * who re-photographs their working can see what the earlier attempt scored, so
 * the re-grade is an audit trail rather than a quiet overwrite.
 */

const props = defineProps<{
  grade: GradeResponse | null;
  marking: boolean;
  history: GradeResponse[];
}>();

interface MarkPoint {
  ok: boolean;
  glyph: string;
  text: string;
}

// The API exposes the grading model's `rationale` as free prose ("which
// mark-scheme points were hit or missed") — there is no structured per-point
// field to read. The ✓/! rows are therefore *recovered* from that prose, and
// only when the recovery is unambiguous: telling a student they earned a mark
// they did not is worse than showing the rationale as written. Anything the
// rules below cannot classify with confidence falls through to the prose.
const BULLET = /^\s*(?:[-*•]|\d+[.)]|\(\d+\))\s+/;
const HIT_GLYPH = /^\s*(?:✓|✔|✅)\s*/;
const MISS_GLYPH = /^\s*(?:✗|✘|❌|×|✕|!)\s*/;
// Scanned over the leading clause only. "Correctly balanced the equation, but
// no state symbols" is a hit whose tail mentions a miss; the verdict lives at
// the front of the sentence, so reading the whole line inverts it.
const MISS_CUE =
  /\b(?:not|no|missed?|missing|omitted|absent|incorrect|wrong|failed|lacks?|lacking|didn'?t|doesn'?t|never|insufficient|unclear)\b/i;
const HIT_CUE =
  /\b(?:awarded|credited|correct(?:ly)?|gained|earned|hit|met|achieved|scored|identified|stated|explained|described|calculated|recognised)\b/i;

// A recovered row must read like a mark-scheme point. Past these bounds the
// text is a paragraph wearing a bullet, and forcing it into a ✓ row would be
// less readable than the prose it came from.
const MAX_POINTS = 8;
const MAX_POINT_CHARS = 220;

function splitRationale(rationale: string): string[] {
  const lines = rationale
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const bullets = lines.filter((l) => BULLET.test(l));
  if (bullets.length > 0) return bullets;
  if (lines.length > 1) return lines;
  // One unbroken blob: sentences are the only available point boundary.
  return (lines[0] ?? "")
    .split(/(?<=[.;])\s+/)
    .filter((s) => s.trim().length > 0);
}

/** `null` when the chunk's polarity cannot be established. */
function classify(chunk: string, allHit: boolean | null): MarkPoint | null {
  const stripped = chunk.replace(BULLET, "").trim();
  const hitGlyph = HIT_GLYPH.test(stripped);
  const missGlyph = MISS_GLYPH.test(stripped);
  const text = stripped.replace(HIT_GLYPH, "").replace(MISS_GLYPH, "").trim();
  if (text.length === 0 || text.length > MAX_POINT_CHARS) return null;

  // The mark totals are structured data; when they pin every point to one
  // polarity they outrank anything the prose implies.
  const ok =
    allHit !== null
      ? allHit
      : hitGlyph
        ? true
        : missGlyph
          ? false
          : (() => {
              const lead = text.split(/[,;—–]|\bbut\b|\bhowever\b/i)[0] ?? text;
              if (MISS_CUE.test(lead)) return false;
              if (HIT_CUE.test(lead)) return true;
              return null;
            })();
  if (ok === null) return null;
  return { ok, glyph: ok ? "✓" : "!", text };
}

const points = computed<MarkPoint[]>(() => {
  const g = props.grade;
  if (g === null || g.rationale.trim() === "") return [];
  const allHit =
    g.marks_available > 0 && g.marks_awarded >= g.marks_available
      ? true
      : g.marks_awarded <= 0
        ? false
        : null;
  const chunks = splitRationale(g.rationale);
  if (chunks.length === 0 || chunks.length > MAX_POINTS) return [];
  const recovered: MarkPoint[] = [];
  for (const chunk of chunks) {
    const point = classify(chunk, allHit);
    // All or nothing: a partial list would silently drop a point the student
    // needs to see.
    if (point === null) return [];
    recovered.push(point);
  }
  return recovered;
});

const verdict = computed<{
  label: string;
  tone: "success" | "info" | "warn" | "neutral";
}>(() => {
  const g = props.grade;
  if (g === null || g.marks_available <= 0)
    return { label: "Marked", tone: "neutral" };
  const fraction = g.marks_awarded / g.marks_available;
  // Deliberately flat, non-punitive language — no grades, no "fail", no
  // congratulation theatre (PRD §8.3).
  if (fraction >= 1) return { label: "Full marks", tone: "success" };
  if (fraction >= 0.7) return { label: "Nearly there", tone: "success" };
  if (fraction >= 0.4) return { label: "Part marks", tone: "info" };
  if (fraction > 0) return { label: "Some credit", tone: "warn" };
  return { label: "Not yet", tone: "warn" };
});

// Precomputed because a flex row that mixes a raw text node with an
// interpolation gets a `gap` on each fragment, which reads as a stray space.
const outOfLabel = computed(() =>
  props.grade === null ? "" : `/ ${props.grade.marks_available} marks`,
);

const historyLabels = computed(() =>
  props.history.map((g, i) => ({
    key: `${g.interaction_id}-${i}`,
    label: `${g.marks_awarded} / ${g.marks_available} marks`,
  })),
);

const renderedFeedback = computed(() =>
  props.grade === null ? "" : renderMarkdown(props.grade.feedback),
);

const renderedRationale = computed(() =>
  props.grade === null ? "" : renderMarkdown(props.grade.rationale),
);
</script>

<template>
  <RlCard class="marking">
    <p class="eyebrow">Marking</p>

    <!-- Kept above every state, so the trail is still there while a re-grade
         is in flight and while the re-opened answer form is empty. -->
    <div
      v-if="historyLabels.length > 0"
      class="history"
      data-testid="practice-grade-history"
    >
      <p class="history-title">Previous attempts</p>
      <ul>
        <li v-for="h in historyLabels" :key="h.key">
          <s>{{ h.label }}</s>
        </li>
      </ul>
    </div>

    <div v-if="marking" class="marking-wait rl-anim-fade-up">
      <p class="wait-status" aria-live="polite">
        <LoaderCircle :size="16" class="rl-spin" aria-hidden="true" />
        <span>Marking against the mark scheme…</span>
      </p>
      <div class="skeleton-score">
        <RlSkeleton width="44px" :height="30" />
        <RlSkeleton width="78px" />
      </div>
      <div class="skeleton-lines">
        <RlSkeleton width="100%" />
        <RlSkeleton width="92%" />
        <RlSkeleton width="80%" />
        <RlSkeleton width="88%" />
      </div>
    </div>

    <div v-else-if="grade === null" class="empty">
      <ClipboardCheck :size="26" aria-hidden="true" />
      <p>Your marking will appear here.</p>
    </div>

    <div v-else class="marked rl-anim-fade-up">
      <div class="score-row">
        <span class="score">{{ grade.marks_awarded }}</span>
        <span class="score-out">{{ outOfLabel }}</span>
        <RlPill :tone="verdict.tone">{{ verdict.label }}</RlPill>
      </div>

      <ul v-if="points.length > 0" class="points" data-testid="marking-points">
        <li v-for="(p, i) in points" :key="i">
          <span class="glyph" :class="p.ok ? 'glyph-hit' : 'glyph-miss'">{{
            p.glyph
          }}</span>
          <span>{{ p.text }}</span>
        </li>
      </ul>
      <template v-else>
        <p class="section-title">Mark scheme</p>
        <div class="prose muted" v-html="renderedRationale" />
      </template>

      <p class="section-title">Feedback</p>
      <div class="prose" v-html="renderedFeedback" />

      <slot />
    </div>
  </RlCard>
</template>

<style scoped>
.marking {
  min-height: 300px;
}

.eyebrow {
  margin: 0 0 var(--sp-3);
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--rl-fg-3);
}

.history {
  padding-bottom: var(--sp-3);
  margin-bottom: var(--sp-3);
  border-bottom: 1px solid var(--rl-border-subtle);
}

.history-title {
  margin: 0 0 var(--sp-1);
  font-size: var(--rl-text-xs);
  color: var(--rl-fg-3);
}

.history ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.history li {
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  min-height: 210px;
  text-align: center;
  color: var(--rl-fg-3);
}

.empty p {
  max-width: 220px;
  margin: 0;
  font-size: var(--rl-text-md);
  line-height: 1.5;
}

.wait-status {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin: 0 0 var(--sp-5);
  font-size: var(--rl-text-md);
  font-weight: 500;
  color: var(--rl-fg-2);
}

.wait-status svg {
  color: var(--rl-green-600);
  flex: none;
}

.skeleton-score {
  display: flex;
  align-items: baseline;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}

.skeleton-lines {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.score-row {
  display: flex;
  align-items: baseline;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}

.score {
  font-size: var(--rl-text-3xl);
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1;
}

.score-out {
  font-size: var(--rl-text-md);
  color: var(--rl-fg-3);
}

.score-row .pill {
  margin-left: auto;
}

.points {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.points li {
  display: flex;
  gap: var(--sp-2);
  font-size: var(--rl-text-md);
  line-height: 1.45;
  color: var(--rl-fg-2);
}

.glyph {
  flex: none;
  font-weight: 700;
}

.glyph-hit {
  color: var(--rl-mint-600);
}

.glyph-miss {
  color: var(--rl-warn-fg);
}

.section-title {
  margin: var(--sp-5) 0 var(--sp-2);
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--rl-fg-3);
}

.prose {
  font-size: var(--rl-text-md);
  line-height: 1.55;
  color: var(--rl-fg-2);
}

.prose :deep(p) {
  margin: 0 0 var(--sp-2);
}

.prose :deep(p:last-child) {
  margin-bottom: 0;
}
</style>
